import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import type { User } from "./types";

interface AuthContextValue {
  user: User | null;
  setupRequired: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const result = await api.me();
    setUser(result.user);
    setSetupRequired(result.setupRequired);
  };

  useEffect(() => {
    refresh()
      .catch(() => {
        setUser(null);
        setSetupRequired(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      setupRequired,
      loading,
      refresh,
      logout
    }),
    [loading, setupRequired, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

export function canManageUsers(user: User | null) {
  return user?.role === "owner" || user?.role === "admin";
}

export function canManageChannels(user: User | null) {
  return user?.role === "owner" || user?.role === "admin";
}

export function canSendCards(user: User | null) {
  return user?.role === "owner" || user?.role === "admin" || user?.role === "editor";
}
